import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AppIcon,
  ContentCompetency,
  ContentNugget,
  NUGGET_THEMES,
  PageHeader,
  Tool,
  toolTransform,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { SuggestedNuggetsSidebar } from '@backrs_ui/grow-ui/src/components/SuggestedNuggetsSidebar/SuggestedNuggetsSidebar';
import { SingleFilterButton } from '@backrs_ui/grow-ui/src/pages/Search/components/SingleFilterButton';

import { Box, SxProps, Theme, TextField, Typography } from '@mui/material';

import { useAgentContext } from './CompetencyPage.agentContext';
import { CompetencyBanner } from '../molecules/CompetencyBanner';

const COMPETENCY_STRINGS = appStrings.modules.competency;

type CompetencyPageProps = Readonly<{
  categoryName: string;
  contentCompetency: ContentCompetency;
  nuggets: ContentNugget[];
  tools: Tool[];
  playlists: PriorityPlaylist[];
}>;

export const CompetencyPage = ({
  categoryName,
  contentCompetency,
  nuggets,
  tools,
  playlists,
}: CompetencyPageProps) => {
  const navigate = useNavigate();
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [isAllThemesSelected, setIsAllThemesSelected] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredNuggets = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const hasSearchTerm = searchTerm.trim();
    const hasSelectedThemes = selectedThemes.length > 0;

    return nuggets.filter((nugget) => {
      // filter by search term
      if (hasSearchTerm) {
        const matchesSearch =
          nugget.title?.toLowerCase().includes(searchLower) ||
          nugget.description?.toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;
      }

      // filter for only nuggets in the selected themes
      if (hasSelectedThemes) {
        const matchesTheme =
          nugget.content_nugget_theme && selectedThemes.includes(nugget.content_nugget_theme);

        if (!matchesTheme) return false;
      }

      return true;
    });
  }, [nuggets, selectedThemes, searchTerm]);

  // add or remove a theme from the selected themes
  const toggleTheme = (nuggetTheme: string) => {
    setSelectedThemes((prev) =>
      prev.includes(nuggetTheme) ? prev.filter((t) => t !== nuggetTheme) : [...prev, nuggetTheme],
    );
    setIsAllThemesSelected(false);
  };

  const toolNuggets = useMemo(() => tools.map(toolTransform), [tools]);

  useAgentContext({
    searchTerm,
    selectedThemes,
    filteredNuggets,
    totalNuggetCount: nuggets.length,
  });

  const competencyHasNoResources = tools.length === 0 && nuggets.length === 0;

  const hasActiveFilter = searchTerm.trim() !== '' || selectedThemes.length > 0;
  const searchHasNoResults = hasActiveFilter && filteredNuggets.length === 0;

  const hasRecommendedItems = playlists.length > 0 || tools.length > 0;

  return (
    <Box sx={containerSx}>
      <Box sx={mainContentSx}>
        <CompetencyBanner contentCompetency={contentCompetency} />
        <PageHeader
          title={contentCompetency.title}
          subtitle={contentCompetency.description}
          tag={categoryName}
          tagId="competency-category-tag"
          onTagClick={() =>
            navigate(`${APP_ROUTE.LIBRARY}?unlock=${contentCompetency.unlock.slug}`)
          }
          rightComponents={[
            <TextField
              id="competency-search-input"
              key="search-field"
              variant="outlined"
              placeholder="Search resources"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <AppIcon iconName="SearchMd" color="currentColor" diameter="20px" />
                ),
              }}
              sx={searchBarSx}
            />,
          ]}
        />

        {!competencyHasNoResources && (
          <Box sx={filterContainerSx}>
            <Box sx={individualFilterContainerSx}>
              <SingleFilterButton
                name={COMPETENCY_STRINGS.filterButtons.allResources}
                iconName="FilterLines"
                isSelected={isAllThemesSelected}
                toggleFilter={() => {
                  setSelectedThemes([]);
                  setIsAllThemesSelected(true);
                }}
              />
            </Box>
            <Box sx={individualFilterGroupContainerSx}>
              <Box sx={individualFilterContainerSx}>
                <SingleFilterButton
                  name={COMPETENCY_STRINGS.filterButtons.learning}
                  iconName="BookOpen01"
                  isSelected={selectedThemes.includes(NUGGET_THEMES.LEARN_AND_EXPLORE)}
                  toggleFilter={() => toggleTheme(NUGGET_THEMES.LEARN_AND_EXPLORE)}
                />
              </Box>
              <Box sx={individualFilterContainerSx}>
                <SingleFilterButton
                  name={COMPETENCY_STRINGS.filterButtons.realPeople}
                  iconName="UserSquare"
                  isSelected={selectedThemes.includes(NUGGET_THEMES.REAL_PEOPLE_REAL_STORIES)}
                  toggleFilter={() => toggleTheme(NUGGET_THEMES.REAL_PEOPLE_REAL_STORIES)}
                />
              </Box>
            </Box>
          </Box>
        )}

        {(searchHasNoResults || competencyHasNoResources) && (
          <Box sx={noResultsContainerStyle}>
            <Box component="img" src="/images/no-search-results.svg" />
            <Typography variant="text-lg-regular">
              {competencyHasNoResources
                ? COMPETENCY_STRINGS.noResults
                : COMPETENCY_STRINGS.noSearchResults}
            </Typography>
          </Box>
        )}

        {filteredNuggets.length > 0 && (
          <NuggetCardList
            nuggets={filteredNuggets}
            onClick={(nugget) => handlePanelNuggetClick(nugget, navigate)}
            direction="row"
          />
        )}

        {hasRecommendedItems && (
          <SuggestedNuggetsSidebar
            pageType="competencyPage"
            nuggets={toolNuggets}
            playlists={playlists}
          />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  width: '100%',
  margin: '0 auto',
};

const mainContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: { xs: '100%', lg: '1240px' },
  margin: { sm: '0 auto' },
  alignItems: 'left',
  gap: backrs.spacing['spacing-xl'],
});

const filterContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: { xs: backrs.spacing['spacing-sm'], sm: backrs.spacing['spacing-xl'] },
  justifyContent: 'space-between',
  my: { xs: backrs.spacing['spacing-sm'], sm: backrs.spacing['spacing-lg'] },
});

const individualFilterGroupContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: { xs: backrs.spacing['spacing-sm'], sm: backrs.spacing['spacing-lg'] },
  flexWrap: 'wrap',
});

const individualFilterContainerSx: SxProps<Theme> = {
  display: 'flex',
  width: 'fit-content',
  height: 'fit-content',
  whiteSpace: 'nowrap',
};

const searchBarSx: SxProps<Theme> = ({ backrs }) => ({
  width: { xs: '100%', md: '200px', lg: '300px' },
  maxWidth: '320px',
  backgroundColor: backrs.colors['Colors/Base/White'],
  marginBottom: backrs.spacing['spacing-lg'],
  '& .MuiInputBase-root': {
    height: '40px',
    border: `1px solid ${backrs.colors['Gray/600']}`,
  },
});

const noResultsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
  whiteSpace: 'pre-line',
  alignSelf: 'center',
  gap: backrs.spacing['spacing-5xl'],
  mt: {
    xs: backrs.spacing['spacing-4xl'],
    sm: backrs.spacing['spacing-8xl'],
  },
  img: {
    width: { xs: '180px', sm: '280px' },
    height: 'auto',
    maxWidth: '100%',
  },
});
